import { openDB, type IDBPDatabase } from "idb";

export type SurveyStatus = "in-progress" | "structured" | "published";

export interface SurveyAnswer {
  questionIndex: number;
  question: string;
  answer: string;
}

export interface Survey {
  id: string;
  templateId: string;
  templateName: string;
  title: string;
  answers: SurveyAnswer[];
  updatedAt: number;
  status: SurveyStatus;
  structuredOutput?: string;
  rawDictation?: string;
  markdownSource?: string;
}

const DB_NAME = "sop-dictation";
const SURVEYS_STORE = "surveys";
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("drafts")) {
          db.createObjectStore("drafts", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("versions")) {
          const store = db.createObjectStore("versions", { keyPath: "id" });
          store.createIndex("byDraft", "draftId");
        }
        if (!db.objectStoreNames.contains(SURVEYS_STORE)) {
          db.createObjectStore(SURVEYS_STORE, { keyPath: "id" });
        }
      },
    });
  }
  return dbPromise;
}

export async function saveSurvey(survey: Survey): Promise<void> {
  const db = await getDB();
  await db.put(SURVEYS_STORE, survey);
}

export async function getSurvey(id: string): Promise<Survey | undefined> {
  const db = await getDB();
  return db.get(SURVEYS_STORE, id);
}

export async function getAllSurveys(): Promise<Survey[]> {
  const db = await getDB();
  const surveys = await db.getAll(SURVEYS_STORE);
  return surveys.sort((a: Survey, b: Survey) => b.updatedAt - a.updatedAt);
}

export async function deleteSurvey(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(SURVEYS_STORE, id);
}
